//replaces widget-container.component.ts

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { result } from "lodash";
import { useDispatch } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { WidgetMessages } from "app/components/widget/widget-types/Widget";
import { useInViewport } from "app/custom-hooks/useInViewport";
import { i18nFilter } from "app/filters/i18nFilter";
import { checkWidgetsLoaded } from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { PptExportService } from "app/services/PptExportService/PptExportService";
import type { IPptExportRequest } from "app/services/PptExportService/PptExportServiceTypes";
import { SwTrack } from "app/services/SwTrack";
import { WidgetContainerTemplateV2 } from "./WidgetContainerTemplate-v2";
import { allTrackers } from "app/services/track/track";

interface INewWidgetInstance extends IWidget<any> {
    container?: string;
    dashboardId?: string;
    _params?: any;
    broadcast?: any;
}

interface IWidgetProps extends IDashboardWidgetInterface<INewWidgetInstance> {
    // Todo: define used widgetProps/ rename
    usePreloader?: boolean;
    // onClose: () => void;
    dispatchWidgetChanges?: boolean;
    isStatic?: boolean;
    onEdit?: (widget: IWidget<any>) => void;
    onToggleEnlarge?: (widget: IWidget<any>) => void;
}

const i18n = i18nFilter();
const getIsTitleClickable = (widgetInstance) => {
    return (
        widgetInstance.readOnly &&
        widgetInstance.viewData?.proUrl?.indexOf(`*${encodeURI(widgetInstance.getWidgetModel().key[0].name)}`) > -1
    );
};

const pptService = new PptExportService();

export const WidgetV2: React.FC<IWidgetProps> = (props) => {
    const { usePreloader, widget, onEdit, dispatchWidgetChanges = true, onToggleEnlarge, setChart, isStatic } = props;
    const dispatch = useDispatch();

    const charts = useRef(undefined);
    function handleSetChart(chartRef) {
        setChart?.(chartRef);
        charts.current = chartRef;
    }

    const [templateLoaded, setTemplateLoaded] = useState(false);
    const [isTitleClickable, setIsTitleClickable] = useState(false);
    const [isTitleEditable, setIsTitleEditable] = useState(false);
    const [shouldHidePreloader, setShouldHidePreloader] = useState(false);
    const preLoaderUserContainer = useRef(undefined);
    const isWidgetInViewPort = useInViewport(preLoaderUserContainer);

    //
    // // Create a new memoized selector, for each component instance, on mount
    // const selectWidgetEnlargedStateByWidgetId = useMemo(
    //     makeSelectWidgetEnlargedStateByWidgetId,
    //     [],
    // );
    // const widgetEnlargedStateByWidgetId = useMemo(selectWidgetEnlargedStateByWidgetId, []);

    // const widgetEnlargedStateByWidgetId = useSelector((state) =>
    //     selectWidgetEnlargedStateByWidgetId(state, widget?.id),
    // );

    // const widgetEnlargedState = useSelector((state) =>
    //     selectWidgetEnlargedStateByWidgetId(state, widget?.id),
    // );
    // const toggledEnlarged = useSelector((state) =>
    //     selectToggledEnlargedByWidgetId(state, widget?.id),
    // );

    // console.log(
    //     `widgetEnlargedStateByWidgetId for ${widget?.id} === ${widgetEnlargedStateByWidgetId}`,
    // );
    // console.log(`toggledEnlarged for ${widget?.id} === ${toggledEnlarged}`);

    function handleClearWidgetFilter(e, options = {} as any) {
        console.log("WidgetV2 clear-widget-filter");
        e.preventDefault();
        const { track = true } = options;
        if (track) {
            SwTrack.all.trackEvent("Button", "click", "Table/Clear all");
        }
        widget._params.filter = null;
        widget.broadcast("clear-utility-filter");
    }

    useEffect(() => {
        widget.on("clear-widget-filter", handleClearWidgetFilter);
        return () => {
            widget.off("clear-widget-filter", handleClearWidgetFilter);
        };
    }, [widget]);

    const widgetTitle = useMemo(() => {
        return widget.dashboardId === DASHBOARD_ID_PREVIEW_MODE &&
            !widget.getWidgetMetricConfig().options?.hasDynamicTitle
            ? i18n(widget.getWidgetMetricConfig().title)
            : widget.viewData.title;
    }, [widget]);

    useEffect(() => {
        const preloaderHeight = widget.getProperties().preloaderHeight;
        setIsTitleClickable(!getIsTitleClickable(widget));
        return () => result(widget, "cleanup");
    }, []);

    const editTitle = useCallback((event) => {
        const el = $(event.target).prev();
        // Todo: handle css
        $(event.target).hide();
        el.css("width", el.val().toString().length * 14);
        el.select();
        setIsTitleEditable(true);
        SwTrack.all.trackEvent("Edit", "click", "widget name");
    }, []);

    const onExportClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "Dashboard Widget Export PPT");
        const request: IPptExportRequest = {
            name: widgetTitle,
            metrics: [widget.getDataForPpt()],
            meta: {
                userEmail: swSettings?.user?.username,
            },
        };
        pptService.exportPpt(request);
    }, [widgetTitle, widget]);

    const cancelEditTitle = useCallback(() => {
        setIsTitleEditable(false);
    }, []);

    // const onWidgetTitleChange = useCallback((event) => {
    //     setWidgetTitle(event.target.value);
    // }, []);

    const saveTitle = useCallback(() => {
        const updatedWidget = widget.getWidgetConfig();
        // change title on config
        updatedWidget.properties.title = widgetTitle;
        // change template to 'custom'
        updatedWidget.properties.titleTemplate = "custom";
        // change title on widget instance
        widget.mergeViewData({ title: widgetTitle });
        DashboardService.editWidget(updatedWidget);
        SwTrack.all.trackEvent("Edit", "click", "widget name/" + widgetTitle);
    }, [widget, widgetTitle]);

    useEffect(() => {
        if (isWidgetInViewPort) {
            if (templateLoaded) {
                // hidePreloader($element);
                setShouldHidePreloader(true);
            }
        }
    }, [templateLoaded, isWidgetInViewPort]);

    const widgetContainerProps = {
        widget,
        widgetTitle,
        titleEditable: isTitleEditable,
        titleClickable: isTitleClickable,
        cancelEditTitle,
        saveTitle,
        editTitle,
        // onWidgetTitleChange,
    };

    const [widgetChangedSignal, setWidgetChangedSignal] = useState(() => Math.random());
    function handleChange(data) {
        setWidgetChangedSignal((prev) => prev + 1);
        if (dispatchWidgetChanges && widget.dashboardId !== DASHBOARD_ID_PREVIEW_MODE) {
            // dispatch(setWidgetUpdated(widget));
            dispatch(checkWidgetsLoaded());
            //     dispatch(setWidgetStateChanged(widget?.dashboardId, widget.id, widget.widgetState));
        }
    }
    useEffect(() => {
        if (widget && dispatchWidgetChanges) {
            widget.on(WidgetMessages.WIDGET_GET_DATA_SUCCESS, handleChange);
            widget.on(WidgetMessages.WIDGET_GET_DATA_FAIL, handleChange);
            // widget.on(WidgetMessages.WIDGET_LODAING, handleChange);
            // widget.on(WidgetMessages.WIDGET_LOADED, handleChange);
            widget.on(WidgetMessages.WIDGET_UPDATED, handleChange);
            widget.on(WidgetMessages.WIDGET_CLEANUP, handleChange);
        }
        return () => {
            if (widget && dispatchWidgetChanges) {
                widget.off(WidgetMessages.WIDGET_GET_DATA_SUCCESS, handleChange);
                widget.off(WidgetMessages.WIDGET_GET_DATA_FAIL, handleChange);
                // widget.off(WidgetMessages.WIDGET_LODAING, handleChange);
                // widget.off(WidgetMessages.WIDGET_LOADED, handleChange);
                widget.off(WidgetMessages.WIDGET_UPDATED, handleChange);
                widget.off(WidgetMessages.WIDGET_CLEANUP, handleChange);
            }
        };
    }, [widget]);

    function handleToggleEnlarged() {
        //add the widget to the event
        onToggleEnlarge(widget);
    }

    // ref={preLoaderUserContainer}
    if (usePreloader) {
        return (
            <div>
                <h3>usePreloader</h3>
                <div
                    className={`widget-preloader-loader swWidget swWidget--top swWidget--boxShadow ${
                        shouldHidePreloader ? "preloader-hidden" : "preloader-visible"
                    }`}
                >
                    <WidgetContainerTemplateV2
                        {...widgetContainerProps}
                        onEdit={onEdit}
                        onToggleEnlarge={handleToggleEnlarged}
                    />
                </div>
                <div
                    className="widgetContainerTemplate"
                    ref={preLoaderUserContainer}
                >
                    <WidgetContainerTemplateV2
                        {...widgetContainerProps}
                        onEdit={onEdit}
                        onToggleEnlarge={handleToggleEnlarged}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="widgetContainerTemplate">
            <WidgetContainerTemplateV2
                widget={widget}
                widgetTitle={widgetTitle}
                titleEditable={isTitleEditable}
                cancelEditTitle={cancelEditTitle}
                saveTitle={saveTitle}
                editTitle={editTitle}
                onEdit={onEdit}
                onToggleEnlarge={handleToggleEnlarged}
                className={classNames(
                    //     widget.pos.sizeX ? "widget-size-" + widget.pos.sizeX : "",
                    widget?.pos?.sizeY ? "widget-height-" + widget.pos.sizeY : "",
                )}
                setChart={handleSetChart}
                isStatic={isStatic}
            />
        </div>
    );
};

export const MemoizedWidgetV2 = React.memo(WidgetV2);
